import { Directive, Input, NgZone, OnInit } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { fromEvent } from 'rxjs';
import { pairwise, switchMap, takeUntil, tap } from 'rxjs/operators';

@Directive({
    // tslint:disable-next-line:directive-selector
    selector: 'mat-tab-group[matTabGroupGesture]'
})
export class MatTabGroupGestureDirective implements OnInit {

    private headers: any;
    private headersList: any;
    private originalHeadersListTransition?: string;
    private headersMaxScroll?: number;

    private body: any;

    @Input() swipeLimitWidth = 80;
    @Input() connectEdges = true;

    constructor(
        private tabGroup: MatTabGroup,
        private zone: NgZone,
    ) {
    }


    ngOnInit(): void {
        this.headers = this.tabGroup._elementRef.nativeElement.querySelector('.mat-mdc-tab-header');
        if (!this.headers) { throw new Error('No headers found in DOM! Aborting...'); }

        this.headersList = this.headers.querySelector('.mat-mdc-tab-list');
        if (!this.headersList) { throw new Error('No headers list found in DOM! Aborting...'); }

        this.body = this.tabGroup._elementRef.nativeElement.querySelector('.mat-mdc-tab-body-wrapper');
        if (!this.body) { throw new Error('No body found in DOM! Aborting...'); }

        // Passive and outside Angular: the browser scrolls without waiting on the page, and a scroll
        // does not redraw the app. Only a tab change goes back into Angular.
        this.zone.runOutsideAngular(() => {
            this._handleHeadersEvents();
            this._handleBodyEvents();
        });
    }

    private _handleHeadersEvents(): void {
        // this will capture all touchstart events from the headers element
        fromEvent(this.headers, 'touchstart', { passive: true })
            .pipe(
                tap(() => {
                    this.originalHeadersListTransition = this.headersList.style.transition;
                    this.headersList.style.transition = 'none';
                    this.headersMaxScroll = -1 * (this.headersList.offsetWidth - this.headers.offsetWidth + 64);
                }),
                switchMap((e) => {
                    // after a mouse down, we'll record all mouse moves
                    return fromEvent(this.headers, 'touchmove', { passive: true })
                        .pipe(
                            // we'll stop (and unsubscribe) once the user releases the mouse
                            // this will trigger a 'mouseup' event
                            takeUntil(fromEvent(this.headers, 'touchend', { passive: true }).pipe(
                                tap(() => this.headersList.style.transition = this.originalHeadersListTransition)
                            )),
                            // pairwise lets us get the previous value to draw a line from
                            // the previous point to the current point
                            pairwise()
                        );
                })
            )
            .subscribe((res: [any, any]) => {
                const rect = this.headers.getBoundingClientRect();
                // previous and current position with the offset
                const prevX = res[0].touches[0].clientX - rect.left;

                const currentX = res[1].touches[0].clientX - rect.left;

                this._scrollHeaders(currentX - prevX);
            });
    }

    private _scrollHeaders(scrollX: number): void {
        if (!this.headersList || !this.headersMaxScroll) { return; }
        const currentTransform = this.headersList.style.transform;
        let currentScroll: number;
        if (currentTransform && currentTransform.indexOf('translateX') > -1) {
            let tmp = currentTransform.substring('translateX('.length);
            tmp = tmp.substring(0, tmp.length - 'px)'.length);
            currentScroll = parseInt(tmp, 10);
        } else { currentScroll = 0; }
        let newScroll = currentScroll + scrollX;
        if (newScroll > 0) { newScroll = 0; }
        if (newScroll < this.headersMaxScroll) { newScroll = this.headersMaxScroll; }
        // this._renderer.setStyle(this._headersList, 'transform', `translateX(${newScroll}px)`);
        this.headersList.style.transform = `translateX(${newScroll}px)`;
    }

    private _handleBodyEvents(): void {
        // Each swipe is measured on its own, from where the finger went down to where it lifted.
        let start: { x: number; y: number } | null = null;
        this.body.addEventListener('touchstart', (e: TouchEvent) => {
            start = e.touches.length === 1 && !this._handlesOwnSwipe(e) ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
        }, { passive: true });
        this.body.addEventListener('touchend', (e: TouchEvent) => {
            if (!start || !e.changedTouches.length) { return; }
            const x = e.changedTouches[0].clientX - start.x;
            const y = e.changedTouches[0].clientY - start.y;
            start = null;
            // A tab change needs a clear sideways swipe: far enough, and at least twice as wide as it is tall.
            if (Math.abs(x) < this.swipeLimitWidth || Math.abs(x) < 2 * Math.abs(y)) { return; }
            this.zone.run(() => x > 0 ? this._prevTab() : this._nextTab());
        }, { passive: true });
    }

    /** A slider, or a box that scrolls sideways (the day view's doctor columns), takes the swipe itself. */
    private _handlesOwnSwipe(e: TouchEvent): boolean {
        const path = e.composedPath();
        return path.slice(0, path.indexOf(this.body)).some((node: any) => {
            if (!(node instanceof HTMLElement)) { return false; }
            if (typeof node.className === 'string' && node.className.indexOf('mat-slider') > -1) { return true; }
            const overflowX = getComputedStyle(node).overflowX;
            return (overflowX === 'auto' || overflowX === 'scroll') && node.scrollWidth > node.clientWidth;
        });
    }

    private _prevTab(): void {
        if (this.tabGroup.selectedIndex === 0 || this.tabGroup.selectedIndex === null) {
            this.tabGroup.selectedIndex = this.connectEdges ? this.tabGroup._tabs.length - 1 : this.tabGroup.selectedIndex;
        }
        else { this.tabGroup.selectedIndex = this.tabGroup.selectedIndex - 1; }
    }

    private _nextTab(): void {
        if (this.tabGroup.selectedIndex === this.tabGroup._tabs.length - 1) {
            this.tabGroup.selectedIndex = this.connectEdges ? 0 : this.tabGroup.selectedIndex;
        }
        else if (this.tabGroup.selectedIndex === null) { this.tabGroup.selectedIndex = 0; }
        else { this.tabGroup.selectedIndex = this.tabGroup.selectedIndex + 1; }
    }

}